import { Search as SearchIcon } from "@mui/icons-material";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Container,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";

import { useEffect, useState } from "react";
import autoInfracaoService from "../../services/autoInfracao";

function AutoInfracao() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [autos, setAutos] = useState([]);
  const [, setPaginacao] = useState(null);
  const [paginaAtual, setPaginaAtual] = useState(1);
  const [pageSize] = useState(10);
  const [filtros, setFiltros] = useState({
    numero: "",
    tipo: "",
    dataInicial: "",
    dataFinal: "",
  });

  const tipoOptions = [
    { value: 0, label: "Multa" },
    { value: 1, label: "Advertência" },
  ];

  const statusOptions = [
    { value: 0, label: "Em Aberto" },
    { value: 1, label: "Pago" },
    { value: 2, label: "Em Recurso" },
    { value: 3, label: "Vencido" },
    { value: 4, label: "Cancelado" },
    { value: 5, label: "Suspenso" },
    { value: 6, label: "Parcelado" },
    { value: 7, label: "Dívida Ativa" },
    { value: 8, label: "Em Análise" },
  ];

  const buscarMeusAutos = async () => {
    setLoading(true);
    setError("");
    try {
      const data = await autoInfracaoService.listarMeusAutos(
        paginaAtual,
        pageSize,
      );
      setAutos(data.data || []);
      setPaginacao(data.pagination || null);
    } catch (err) {
      console.error("Erro ao buscar autos de infração:", err);
      setError(err.message || "Erro ao buscar autos de infração");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    buscarMeusAutos();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paginaAtual, pageSize]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFiltros((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setPaginaAtual(1);
    buscarMeusAutos();
  };

  return (
    <Container sx={{ px: { xs: 2, sm: 3 } }}>
      <Box sx={{ mt: { xs: 2, sm: 4 }, mb: { xs: 2, sm: 4 } }}>
        <Paper elevation={0} sx={{ p: { xs: 2, sm: 3, md: 4 }, borderRadius: 2 }}>
          <Typography
            variant="h4"
            component="h1"
            gutterBottom
            sx={{ fontSize: { xs: "1.2rem", sm: "1.5rem", md: "2.125rem" } }}
          >
            Meus Autos de Infração
          </Typography>

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          <form onSubmit={handleSubmit}>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  fullWidth
                  label="Número"
                  name="numero"
                  value={filtros.numero}
                  onChange={handleChange}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <FormControl fullWidth>
                  <InputLabel>Tipo</InputLabel>
                  <Select
                    name="tipo"
                    value={filtros.tipo}
                    onChange={handleChange}
                    label="Tipo"
                  >
                    <MenuItem value="">Todos</MenuItem>
                    <MenuItem value="0">Multa</MenuItem>
                    <MenuItem value="1">Advertência</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6} md={2}>
                <TextField
                  fullWidth
                  label="Data Inicial"
                  name="dataInicial"
                  type="date"
                  value={filtros.dataInicial}
                  onChange={handleChange}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={2}>
                <TextField
                  fullWidth
                  label="Data Final"
                  name="dataFinal"
                  type="date"
                  value={filtros.dataFinal}
                  onChange={handleChange}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={12} sm={12} md={2}>
                <Button
                  type="submit"
                  variant="contained"
                  startIcon={<SearchIcon />}
                  disabled={loading}
                  fullWidth
                  sx={{ height: { xs: "auto", md: "100%" }, py: { xs: 1.25, md: 0 } }}
                >
                  {loading ? "Buscando..." : "Buscar"}
                </Button>
              </Grid>
            </Grid>
          </form>

          <TableContainer sx={{ mt: 4, overflowX: "auto" }}>
            <Table>
              <TableHead sx={{ bgcolor: "action.hover" }}>
                <TableRow>
                  <TableCell>Descrição</TableCell>
                  <TableCell>Tipo</TableCell>
                  <TableCell>Empreendimento</TableCell>
                  <TableCell>Valor</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Vencimento</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={6} align="center">
                      <CircularProgress />
                    </TableCell>
                  </TableRow>
                ) : autos.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} align="center">
                      Nenhum auto de infração encontrado
                    </TableCell>
                  </TableRow>
                ) : (
                  autos.map((auto) => (
                    <TableRow key={auto.data}>
                      <TableCell>{auto.descricao}</TableCell>
                      <TableCell>
                        {tipoOptions.find((t) => t.value === auto.tipo)
                          ?.label || "-"}
                      </TableCell>
                      <TableCell>{auto.nomeEmpreendimento}</TableCell>
                      <TableCell>
                        {auto.valor
                          ? `R$ ${parseFloat(auto.valor).toFixed(2)}`
                          : "-"}
                      </TableCell>
                      <TableCell>
                        {statusOptions.find((s) => s.value === auto.status)
                          ?.label || "-"}
                      </TableCell>
                      <TableCell>
                        {auto.vencimento
                          ? new Date(auto.vencimento).toLocaleDateString()
                          : "-"}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      </Box>
    </Container>
  );
}

export default AutoInfracao;
